/**
 * Content-keyed toast gate for WS frames that carry NO stable event id.
 *
 * The stable-id dedup in pushAlert can only engage when the backend frame
 * carries a DB notification id. Live risk alerts (event_publisher.publish_alert
 * — e.g. the per-account "Cash exit needs group ownership repair" storm,
 * 2026-08-14: 15 accounts × 15-min backend re-arm = recurring toast spam) have
 * no id, so identical content used to toast on every emission. Identical
 * title+body carries no new information — one toast per window is the contract.
 */
import * as Sentry from "@sentry/nextjs";

const seenAt = new Map<string, number>();
const DEFAULT_WINDOW_MS = 15 * 60_000;
const MAX_KEYS = 500;

export function shouldToastOnce(key: string, windowMs: number = DEFAULT_WINDOW_MS): boolean {
  const now = Date.now();
  const last = seenAt.get(key);
  if (last !== undefined && now - last < windowMs) {
    return false;
  }
  // Bound the cache for long-lived sessions: drop expired entries first,
  // then oldest-inserted if a pathological key volume still exceeds the cap.
  if (seenAt.size >= MAX_KEYS) {
    for (const [k, at] of seenAt) {
      if (now - at >= DEFAULT_WINDOW_MS) seenAt.delete(k);
    }
    while (seenAt.size >= MAX_KEYS) {
      const oldest = seenAt.keys().next().value;
      if (oldest === undefined) break;
      seenAt.delete(oldest);
    }
  }
  seenAt.set(key, now);
  return true;
}

/**
 * Error-class alert toasts must never be invisible to engineering: capture the
 * shown occurrence (post-dedup, so a 15-account storm reports once per window,
 * not fifteen times) with the frame fields needed to trace the emitter.
 */
export function captureAlertToast(
  title: string,
  payload: Record<string, unknown>,
): void {
  try {
    Sentry.captureMessage(`alert-toast: ${title}`, {
      level: "error",
      tags: {
        feature_area: "ws-alerts",
        alert_kind: typeof payload.kind === "string" ? payload.kind : "unknown",
      },
      extra: { payload },
    });
  } catch {
    // telemetry must never break the toast path
  }
}

/** Test seam: reset the dedup window between test cases. */
export function resetToastOnce(): void {
  seenAt.clear();
}
